import "./ResightingAssignments.css";
import { useContext, useEffect, useState } from "react";
import { Alert, Card, CardContent, Snackbar } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import AppContext from "../../Context/AppContext";
import ResightingRecordDetails from "./ResightingRecordDetails";
import { buildRecordDetailsFromApi } from "./recordDetailsUtils";

function ResightingQCRecord() {
  const BASE_URL_LOC = process.env.REACT_APP_SERVER_LOCATION;
  const appStates = useContext(AppContext);
  const navigate = useNavigate();
  const { recordId: recordIdFromUrl } = useParams();
  const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);
  const [selectedAssignmentDetails, setSelectedAssignmentDetails] = useState(null);
  const [isLoadingAssignmentDetails, setIsLoadingAssignmentDetails] = useState(false);
  const [assignmentDetailsError, setAssignmentDetailsError] = useState("");
  const [markStatus, setMarkStatus] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");

  const showToast = (message, severity = "success") => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const fetchAssignmentDetails = async (id) => {
    setIsLoadingAssignmentDetails(true);
    setAssignmentDetailsError("");
    setSelectedAssignmentId(id);
    setSelectedAssignmentDetails(null);
    setMarkStatus(null);
    setRemarks("");

    try {
      const authToken = appStates.getToken();
      if (!authToken) {
        setAssignmentDetailsError("Session expired. Please log in again.");
        return;
      }

      const response = await fetch(`${BASE_URL_LOC}/api/resighting/${id}`, {
        method: "GET",
        redirect: "follow",
        headers: {
          "authentication-token": authToken,
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Headers": "authentication-token",
          "Access-Control-Expose-Headers": "authentication-token",
        },
        mode: "cors",
      });

      const body = await response.json();

      if (!response.ok) {
        setAssignmentDetailsError(
          body?.error || "Unable to load this assignment right now."
        );
        return;
      }

      if (!body?.data) {
        setAssignmentDetailsError("No details found for this assignment.");
        return;
      }

      setSelectedAssignmentDetails(buildRecordDetailsFromApi(body.data));
    } catch (error) {
      setAssignmentDetailsError("Unable to load this assignment right now.");
    } finally {
      setIsLoadingAssignmentDetails(false);
    }
  };

  const handleBackToDashboard = () => {
    setSelectedAssignmentId(null);
    setSelectedAssignmentDetails(null);
    setIsLoadingAssignmentDetails(false);
    setAssignmentDetailsError("");
    navigate("/home/resighting/qc");
  };

  useEffect(() => {
    if (!recordIdFromUrl) {
      return;
    }

    const parsedRecordId = Number(recordIdFromUrl);
    if (!Number.isInteger(parsedRecordId) || parsedRecordId <= 0) {
      setAssignmentDetailsError("Invalid record selected.");
      setSelectedAssignmentId(null);
      return;
    }

    fetchAssignmentDetails(parsedRecordId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordIdFromUrl]);

  const handleSaveRecord = async () => {
    if (!markStatus) {
      showToast("Please select how to mark this record.", "warning");
      return;
    }

    if (markStatus === "blocked" && !remarks.trim()) {
      showToast("Please add remarks before saving a blocked record.", "warning");
      return;
    }

    const authToken = appStates.getToken();
    if (!authToken) {
      showToast("Session expired. Please log in again.", "error");
      return;
    }

    const statusMap = {
      copied: "copied_to_avifauna",
      duplicate: "duplicate",
      blocked: "blocked",
    };

    const qcStatus = statusMap[markStatus];
    if (!qcStatus) {
      showToast("Invalid record status selected.", "error");
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetch(
        `${BASE_URL_LOC}/api/resighting/${selectedAssignmentId}/qc/submission`,
        {
          method: "POST",
          redirect: "follow",
          headers: {
            "Content-Type": "application/json",
            "authentication-token": authToken,
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Headers": "authentication-token",
            "Access-Control-Expose-Headers": "authentication-token",
          },
          mode: "cors",
          body: JSON.stringify({
            qc_status: qcStatus,
            qc_remarks: remarks.trim() || null,
          }),
        }
      );

      const body = await response.json();
      const backendMessage =
        body?.message ||
        body?.data?.message ||
        (typeof body?.data === "string" ? body.data : "") ||
        body?.error;

      if (!response.ok) {
        showToast(backendMessage || "Unable to save this record right now.", "error");
        return;
      }

      showToast(backendMessage || "Record saved successfully.", "success");
      handleBackToDashboard();
    } catch (error) {
      showToast("Unable to save this record right now.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const record = selectedAssignmentDetails;

  const detailsReady =
    !isLoadingAssignmentDetails &&
    !assignmentDetailsError &&
    selectedAssignmentDetails != null;

  const canSave =
    detailsReady &&
    !!markStatus &&
    (markStatus !== "blocked" || remarks.trim().length > 0);

  const saveDisabled = isSaving || !canSave;

  return (
    <div className="resighting-qc-page">
      <div
        className="resighting-qc-detail-header-wrap"
        style={{ marginBottom: "0.7rem" }}
      >
        <div className="resighting-qc-detail-header">
          <h2 className="resighting-qc-section-title">
            Record #{selectedAssignmentId ?? recordIdFromUrl ?? "—"}
          </h2>
          <button
            type="button"
            className="resighting-qc-btn"
            onClick={handleBackToDashboard}
          >
            Back
          </button>
        </div>
        <p className="resighting-admin-paragraph">
          Copy these fields into Avifauna, then mark the outcome below.
        </p>

        {selectedAssignmentDetails?.adminRemarks ? (
          <p className="resighting-qc-blocker-remarks-wrap">
            Admin remarks: {selectedAssignmentDetails.adminRemarks}
          </p>
        ) : null}
      </div>

      {isLoadingAssignmentDetails ? (
        <Card className="resighting-qc-card">
          <CardContent>
            <p className="resighting-qc-muted">Loading record details...</p>
          </CardContent>
        </Card>
      ) : assignmentDetailsError ? (
        <Card className="resighting-qc-card">
          <CardContent>
            <p className="resighting-qc-muted">{assignmentDetailsError}</p>
          </CardContent>
        </Card>
      ) : (
        <ResightingRecordDetails record={record} />
      )}

      <div
        className="resighting-qc-card"
        key={`qc-mark-${selectedAssignmentId ?? recordIdFromUrl ?? "record"}`}
      >
        <h3 className="resighting-admin-heading">Mark this record</h3>
        <div className="resighting-qc-radio-group">
          <label className="resighting-qc-radio-option">
            <input
              type="radio"
              name="record-status"
              value="copied"
              autoComplete="off"
              checked={markStatus === "copied"}
              onChange={(e) => setMarkStatus(e.target.value)}
            />
            Copied to Avifauna
          </label>
          <label className="resighting-qc-radio-option">
            <input
              type="radio"
              name="record-status"
              value="duplicate"
              autoComplete="off"
              checked={markStatus === "duplicate"}
              onChange={(e) => setMarkStatus(e.target.value)}
            />
            Duplicate
          </label>
          <label className="resighting-qc-radio-option">
            <input
              type="radio"
              name="record-status"
              value="blocked"
              autoComplete="off"
              checked={markStatus === "blocked"}
              onChange={(e) => setMarkStatus(e.target.value)}
            />
            Blocked
          </label>
        </div>
        <div className="resighting-qc-remarks-wrap">
          <label className="resighting-qc-record-id" htmlFor="resighting-remarks">
            Remarks {markStatus === "blocked" ? "*" : ""}
          </label>
          <textarea
            id="resighting-remarks"
            className="resighting-qc-remarks-input"
            style={{ marginTop: "0.5rem", fontFamily: "inherit" }}
            rows={4}
            value={remarks}
            autoComplete="off"
            aria-required={markStatus === "blocked"}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder={
              markStatus === "blocked"
                ? "Required: describe what's blocking you"
                : markStatus
                  ? "Optional"
                  : "Select an outcome above first"
            }
          />
          {markStatus === "blocked" && !remarks.trim() ? (
            <p className="resighting-qc-muted" style={{ marginTop: "0.35rem", marginBottom: 0 }}>
              Remarks are required when marking as Blocked.
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className="resighting-qc-btn"
          onClick={handleSaveRecord}
          disabled={saveDisabled}
        >
          {isSaving ? "Saving..." : "Save"}
        </button>
      </div>

      <Snackbar
        open={toastOpen}
        autoHideDuration={10000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity={toastSeverity}
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default ResightingQCRecord;
